import React, { useState, useEffect } from 'react';
import { 
  Package, 
  Search, 
  Filter, 
  ChevronLeft, 
  ChevronRight,
  Truck,
  CheckCircle,
  XCircle,
  Clock,
  Eye,
  Download,
  X,
  MessageSquare,
  Trash2,
  Star
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { toast } from 'sonner';
import api from '../../utils/api';
import { getApiErrorMessage } from '../../utils/errorMessage';

const AdminOrders = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [invoiceLoadingOrderId, setInvoiceLoadingOrderId] = useState(null);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [showReviewsModal, setShowReviewsModal] = useState(false);
  const [selectedOrderForReviews, setSelectedOrderForReviews] = useState(null);
  const [orderReviews, setOrderReviews] = useState([]);
  const [loadingReviews, setLoadingReviews] = useState(false);
  const [deletingReviewId, setDeletingReviewId] = useState(null);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    pages: 0
  });
  const [filters, setFilters] = useState({
    status: '',
    payment_status: '',
    search: '',
    from_date: '',
    to_date: ''
  });

  useEffect(() => {
    fetchOrders();
  }, [pagination.page, filters]);

  const fetchOrders = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        page: pagination.page,
        limit: pagination.limit,
        ...(filters.status && { status: filters.status }),
        ...(filters.payment_status && { payment_status: filters.payment_status }),
        ...(filters.search && { search: filters.search }),
        ...(filters.from_date && { from_date: filters.from_date }),
        ...(filters.to_date && { to_date: filters.to_date })
      });

      const response = await api.get(`/orders/admin?${params}`);
      setOrders(response.data.orders || []);
      setPagination((prev) => ({
        ...prev,
        total: response.data.pagination?.total || 0,
        pages: response.data.pagination?.pages || 1,
      }));
    } catch (error) {
      toast.error('Failed to fetch orders');
    } finally {
      setLoading(false);
    }
  };

  const handleStatusUpdate = async (orderId, statusData) => {
    try {
      await api.put(`/orders/admin/${orderId}/status`, statusData);
      toast.success('Order status updated successfully');
      setShowStatusModal(false);
      fetchOrders();
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Failed to update status'));
    }
  };

  const handleInvoice = async (orderId) => {
    try {
      setInvoiceLoadingOrderId(orderId);
      await api.post(`/orders/admin/${orderId}/invoice`);
      const pdfResponse = await api.get(`/orders/admin/${orderId}/invoice/pdf`, {
        responseType: 'blob',
      });
      const blobUrl = window.URL.createObjectURL(new Blob([pdfResponse.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = `invoice_${orderId}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(blobUrl);
      toast.success('Invoice download started');
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Failed to generate/download invoice'));
    } finally {
      setInvoiceLoadingOrderId(null);
    }
  };

  const fetchOrderReviews = async (order) => {
    try {
      setLoadingReviews(true);
      setSelectedOrderForReviews(order);
      const response = await api.get(`/reviews/admin/order/${order.id}`);
      setOrderReviews(response.data?.reviews || []);
      setShowReviewsModal(true);
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Failed to load reviews'));
    } finally {
      setLoadingReviews(false);
    }
  };

  const handleDeleteReview = async (reviewId) => {
    try {
      setDeletingReviewId(reviewId);
      await api.delete(`/reviews/admin/${reviewId}`);
      setOrderReviews((prev) => prev.filter((r) => r.id !== reviewId));
      toast.success('Review deleted');
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Failed to delete review'));
    } finally {
      setDeletingReviewId(null);
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      'pending_payment': { color: 'bg-yellow-100 text-yellow-800', icon: Clock },
      'payment_pending': { color: 'bg-yellow-100 text-yellow-800', icon: Clock },
      'processing': { color: 'bg-blue-100 text-blue-800', icon: Package },
      'shipped': { color: 'bg-purple-100 text-purple-800', icon: Truck },
      'in_transit': { color: 'bg-indigo-100 text-indigo-800', icon: Truck },
      'out_for_delivery': { color: 'bg-indigo-100 text-indigo-800', icon: Truck },
      'delivered': { color: 'bg-green-100 text-green-800', icon: CheckCircle },
      'cancelled': { color: 'bg-red-100 text-red-800', icon: XCircle }
    };
    const normalized = (status || '').toLowerCase();
    const config = statusConfig[normalized] || { color: 'bg-gray-100 text-gray-800', icon: Package };
    const Icon = config.icon;
    
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${config.color}`}>
        <Icon className="h-3 w-3" />
        {(status || '').toString().replace(/_/g, ' ')}
      </span>
    );
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-amber-950">Orders</h1>
          <p className="text-amber-700 mt-1">Manage and track customer orders</p>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white border-2 border-amber-200 rounded-2xl p-6 shadow-sm">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label className="text-sm font-medium text-amber-900 mb-1 block">Search</label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-amber-500" />
              <Input
                placeholder="Order #, Customer, Email"
                value={filters.search}
                onChange={(e) => setFilters({...filters, search: e.target.value})}
                className="pl-10 border-amber-200"
              />
            </div>
          </div>
          <div>
            <label className="text-sm font-medium text-amber-900 mb-1 block">Order Status</label>
            <select
              value={filters.status}
              onChange={(e) => setFilters({...filters, status: e.target.value})}
              className="w-full border-2 border-amber-200 rounded-xl p-2.5 bg-white"
            >
              <option value="">All Status</option>
              <option value="payment_pending">Pending / Payment Pending</option>
              <option value="processing">Processing</option>
              <option value="shipped">Shipped</option>
              <option value="in_transit">In Transit</option>
              <option value="out_for_delivery">Out for Delivery</option>
              <option value="delivered">Delivered</option>
              <option value="cancelled">Cancelled</option>
            </select>
          </div>
          <div>
            <label className="text-sm font-medium text-amber-900 mb-1 block">Payment Status</label>
            <select
              value={filters.payment_status}
              onChange={(e) => setFilters({...filters, payment_status: e.target.value})}
              className="w-full border-2 border-amber-200 rounded-xl p-2.5 bg-white"
            >
              <option value="">All Payments</option>
              <option value="pending">Pending</option>
              <option value="success">Success</option>
              <option value="failed">Failed</option>
            </select>
          </div>
          <div className="flex items-end">
            <Button
              onClick={fetchOrders}
              className="bg-amber-600 hover:bg-amber-700 text-white w-full"
            >
              <Filter className="h-4 w-4 mr-2" />
              Apply Filters
            </Button>
          </div>
        </div>
      </div>

      {/* Orders Table */}
      <div className="bg-white border-2 border-amber-200 rounded-2xl shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[980px]">
            <thead className="bg-amber-50">
              <tr>
                <th className="px-6 py-4 text-left text-sm font-semibold text-amber-900">Order #</th>
                <th className="px-6 py-4 text-left text-sm font-semibold text-amber-900">Date</th>
                <th className="px-6 py-4 text-left text-sm font-semibold text-amber-900">Customer</th>
                <th className="px-6 py-4 text-left text-sm font-semibold text-amber-900">Items</th>
                <th className="px-6 py-4 text-left text-sm font-semibold text-amber-900">Total</th>
                <th className="px-6 py-4 text-left text-sm font-semibold text-amber-900">Status</th>
                <th className="px-6 py-4 text-left text-sm font-semibold text-amber-900">Payment</th>
                <th className="px-6 py-4 text-left text-sm font-semibold text-amber-900">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-amber-100">
              {loading ? (
                <tr>
                  <td colSpan="8" className="px-6 py-8 text-center text-amber-600">
                    <div className="flex justify-center">
                      <div className="animate-spin rounded-full h-8 w-8 border-4 border-amber-600 border-t-transparent"></div>
                    </div>
                  </td>
                </tr>
              ) : orders.length === 0 ? (
                <tr>
                  <td colSpan="8" className="px-6 py-8 text-center text-amber-600">
                    No orders found
                  </td>
                </tr>
              ) : (
                orders.map((order) => (
                  <tr key={order.id} className="hover:bg-amber-50/50">
                    <td className="px-6 py-4">
                      <span className="font-mono font-medium text-amber-900">
                        {order.order_number}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-sm text-amber-700">
                      {new Date(order.created_at).toLocaleDateString('en-IN')}
                    </td>
                    <td className="px-6 py-4">
                      <div>
                        <p className="font-medium text-amber-900">{order.user_name}</p>
                        <p className="text-xs text-amber-600">{order.user_email}</p>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-sm text-amber-700">
                      {order.items.length} item{order.items.length > 1 ? 's' : ''}
                    </td>
                    <td className="px-6 py-4">
                      <span className="font-bold text-amber-900">
                        ₹{order.total.toFixed(2)}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      {getStatusBadge(order.order_status)}
                    </td>
                    <td className="px-6 py-4">
                      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                        order.payment_status === 'success' ? 'bg-green-100 text-green-800' :
                        order.payment_status === 'failed' ? 'bg-red-100 text-red-800' :
                        'bg-yellow-100 text-yellow-800'
                      }`}>
                        {order.payment_status}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex flex-wrap gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            setSelectedOrder(order);
                            setShowStatusModal(true);
                          }}
                          className="border-amber-300 text-amber-700"
                        >
                          Update Status
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => fetchOrderReviews(order)}
                          className="text-amber-700"
                          title="Manage reviews"
                        >
                          <MessageSquare className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleInvoice(order.id)}
                          className="text-amber-600"
                          disabled={invoiceLoadingOrderId === order.id}
                        >
                          {invoiceLoadingOrderId === order.id ? '...' : <Download className="h-4 w-4" />}
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {pagination.pages > 1 && (
          <div className="px-6 py-4 border-t border-amber-200 flex flex-col sm:flex-row gap-3 sm:justify-between sm:items-center">
            <p className="text-sm text-amber-700">
              Showing {((pagination.page - 1) * pagination.limit) + 1} to{' '}
              {Math.min(pagination.page * pagination.limit, pagination.total)} of {pagination.total} orders
            </p>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPagination({...pagination, page: pagination.page - 1})}
                disabled={pagination.page === 1}
                className="border-amber-300"
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <span className="px-4 py-2 text-sm text-amber-700">
                Page {pagination.page} of {pagination.pages}
              </span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPagination({...pagination, page: pagination.page + 1})}
                disabled={pagination.page === pagination.pages}
                className="border-amber-300"
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Status Update Modal */}
      {showStatusModal && selectedOrder && (
        <StatusUpdateModal
          order={selectedOrder}
          onClose={() => setShowStatusModal(false)}
          onUpdate={handleStatusUpdate}
        />
      )}

      {showReviewsModal && selectedOrderForReviews && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-3xl w-full p-6 max-h-[85vh] overflow-auto">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-bold text-amber-950">
                Reviews - {selectedOrderForReviews.order_number}
              </h3>
              <button
                onClick={() => setShowReviewsModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            {loadingReviews ? (
              <p className="text-amber-700">Loading reviews...</p>
            ) : orderReviews.length === 0 ? (
              <p className="text-amber-700">No reviews for this order.</p>
            ) : (
              <div className="space-y-3">
                {orderReviews.map((review) => (
                  <div key={review.id} className="border border-amber-200 rounded-xl p-4 bg-amber-50/40">
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <p className="font-semibold text-amber-950">{review.product_name || review.product_id}</p>
                        <p className="text-sm text-amber-700">{review.user_name || 'Customer'}</p>
                        <div className="flex items-center gap-1 mt-1">
                          {[1, 2, 3, 4, 5].map((v) => (
                            <Star
                              key={`${review.id}-${v}`}
                              className={`h-4 w-4 ${v <= Number(review.rating || 0) ? 'text-amber-500 fill-amber-500' : 'text-amber-200'}`}
                            />
                          ))}
                        </div>
                        {review.comment ? (
                          <p className="text-sm text-amber-800 mt-2">{review.comment}</p>
                        ) : null}
                      </div>
                      <Button
                        variant="outline"
                        size="sm"
                        className="border-red-300 text-red-700 hover:bg-red-50"
                        disabled={deletingReviewId === review.id}
                        onClick={() => handleDeleteReview(review.id)}
                      >
                        <Trash2 className="h-4 w-4 mr-1" />
                        {deletingReviewId === review.id ? 'Deleting...' : 'Delete'}
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const StatusUpdateModal = ({ order, onClose, onUpdate }) => {
  const [status, setStatus] = useState(order.order_status);
  const [trackingNumber, setTrackingNumber] = useState(order.tracking_number || '');
  const [courierName, setCourierName] = useState(order.courier_name || '');
  const [trackingUrl, setTrackingUrl] = useState(order.tracking_url || '');
  const [estimatedDelivery, setEstimatedDelivery] = useState(order.estimated_delivery || '');
  const [cancellationReason, setCancellationReason] = useState(order.cancellation_reason || '');
  const [note, setNote] = useState('');

  const handleSubmit = () => {
    const updateData = {
      status,
      ...(trackingNumber && { tracking_number: trackingNumber }),
      ...(courierName && { courier_name: courierName }),
      ...(trackingUrl && { tracking_url: trackingUrl }),
      ...(estimatedDelivery && { estimated_delivery: estimatedDelivery }),
      ...(cancellationReason && { cancellation_reason: cancellationReason }),
      ...(note && { note })
    };
    onUpdate(order.id, updateData);
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl max-w-2xl w-full p-6">
        <div className="flex justify-between items-center mb-6">
          <h3 className="text-xl font-bold text-amber-950">
            Update Order Status - {order.order_number}
          </h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="space-y-4">
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
            <h4 className="font-semibold text-amber-900 mb-2">Order Details</h4>
            <p className="text-sm text-amber-800">
              Customer: <span className="font-medium">{order.user_name || '-'}</span> ({order.user_email || '-'})
            </p>
            <p className="text-sm text-amber-800">
              Total: <span className="font-medium">Rs {Number(order.total || 0).toFixed(2)}</span>
            </p>
            <div className="mt-2 space-y-1">
              {(order.items || []).map((item) => (
                <div key={`${order.id}-${item.variant_id}`} className="text-sm text-amber-800">
                  {item.product_name || item.variant_id} ({item.variant_size || item.variant_unit || '-'}) x {item.quantity} - Rs {Number(item.total || 0).toFixed(2)}
                </div>
              ))}
            </div>
            {order.shipping_address && (
              <div className="mt-3 text-sm text-amber-800">
                <p className="font-medium">Shipping Address</p>
                <p>{order.shipping_address.name}</p>
                <p>{order.shipping_address.address_line1}</p>
                {order.shipping_address.address_line2 && <p>{order.shipping_address.address_line2}</p>}
                <p>{order.shipping_address.city}, {order.shipping_address.state} - {order.shipping_address.pincode}</p>
                <p>{order.shipping_address.phone}</p>
              </div>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-amber-900 mb-1">
              Order Status *
            </label>
            <select
              value={status}
              onChange={(e) => setStatus(e.target.value)}
              className="w-full border-2 border-amber-200 rounded-xl p-2.5 bg-white"
            >
              <option value="payment_pending">Pending / Payment Pending</option>
              <option value="processing">Processing</option>
              <option value="shipped">Shipped</option>
              <option value="in_transit">In Transit</option>
              <option value="out_for_delivery">Out for Delivery</option>
              <option value="delivered">Delivered</option>
              <option value="cancelled">Cancelled</option>
            </select>
          </div>

          {status === 'shipped' || status === 'in_transit' || status === 'out_for_delivery' ? (
            <>
              <div>
                <label className="block text-sm font-medium text-amber-900 mb-1">
                  Tracking Number
                </label>
                <Input
                  value={trackingNumber}
                  onChange={(e) => setTrackingNumber(e.target.value)}
                  placeholder="Enter tracking number"
                  className="border-amber-200"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-amber-900 mb-1">
                  Courier Name
                </label>
                <Input
                  value={courierName}
                  onChange={(e) => setCourierName(e.target.value)}
                  placeholder="e.g., Delhivery, Bluedart, etc."
                  className="border-amber-200"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-amber-900 mb-1">
                  Tracking URL
                </label>
                <Input
                  value={trackingUrl}
                  onChange={(e) => setTrackingUrl(e.target.value)}
                  placeholder="https://..."
                  className="border-amber-200"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-amber-900 mb-1">
                  Estimated Delivery Date
                </label>
                <Input
                  type="date"
                  value={estimatedDelivery}
                  onChange={(e) => setEstimatedDelivery(e.target.value)}
                  className="border-amber-200"
                />
              </div>
            </>
          ) : null}

          {status === 'cancelled' && (
            <div>
              <label className="block text-sm font-medium text-amber-900 mb-1">
                Cancellation Reason
              </label>
              <textarea
                value={cancellationReason}
                onChange={(e) => setCancellationReason(e.target.value)}
                placeholder="Reason for cancellation"
                rows={3}
                className="w-full border-2 border-amber-200 rounded-xl p-2.5 bg-white"
              />
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-amber-900 mb-1">
              Additional Notes (Optional)
            </label>
            <textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Add any notes about this status update"
              rows={2}
              className="w-full border-2 border-amber-200 rounded-xl p-2.5 bg-white"
            />
          </div>
        </div>

        <div className="flex gap-3 mt-6">
          <Button
            onClick={handleSubmit}
            className="flex-1 bg-amber-600 hover:bg-amber-700 text-white"
          >
            Update Status
          </Button>
          <Button
            variant="outline"
            onClick={onClose}
            className="flex-1 border-amber-300 text-amber-700"
          >
            Cancel
          </Button>
        </div>
      </div>
    </div>
  );
};

export default AdminOrders;
